import React, { useRef, useState } from 'react';
import { FieldName, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  apiDataChangeRequestUserService,
  IApiDataChangeRequestUser,
} from '@api/services/dataChangeRequestUser.apiService';
import { ButtonNew } from '@components-library/ButtonNew/ButtonNew';
import { DocumentPreview } from '@components-member/shared/DropzoneDocumentPreview/DocumentPreview/DocumentPreview';
import { DropZone } from '@components-shared/DropZone';
import { IFormField } from '@components-shared/forms/formField.interface';
import generator from '@components-shared/forms/formGenerator';
import { NotificationDialog } from '@components-shared/modal';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { observer } from 'mobx-react';
import styles from './userUpdateBankDetails.styles.module.scss';

const fields: IFormField[] = [
  {
    name: 'bankBranchName',
    title: keyMap.settingsUserFormBankDetailsBankBranchNameTitle,
    required: true,
    className: 'sm-6',
  },
  {
    name: 'beneficiaryBankName',
    title: keyMap.settingsUserFormBankDetailsBeneficiaryBankNameTitle,
    required: true,
    className: 'sm-6',
  },
  {
    name: 'beneficiaryAccountNumber',
    title: keyMap.settingsUserFormBankDetailsBeneficiaryAccountNumberTitle,
    hideNumericArrows: true,
    required: true,
    className: 'sm-12',
  },
];

const Form = generator(fields);

type IFormApiDataChangeRequestUser = Omit<IApiDataChangeRequestUser, 'bankCardFileItem'> & {
  bankCardFileItem: File | null;
};

export const UserUpdateBankDetailsForm = observer(
  ({
    onClose,
    onSuccess,
    dropzoneTitle,
  }: {
    onSuccess: () => Promise<void>;
    onClose?: (state: boolean) => void;
    dropzoneTitle?: string;
  }) => {
    const { i18n } = useTranslation();

    const [processing, setProcessing] = useState(false);

    const { watch, setValue, handleSubmit } = useForm<IFormApiDataChangeRequestUser>({
      defaultValues: {
        beneficiaryBankName: '',
        beneficiaryAccountNumber: '',
        bankBranchName: '',
        bankCardFileItem: null,
      },
    });

    const formRef = useRef<typeof Form>(null);

    const handleChange = (
      fieldName: FieldName<Omit<IFormApiDataChangeRequestUser, 'bankCardFileItem'>>,
      value: string,
    ) => {
      setValue(fieldName, value);
    };

    const formState = watch();

    const isValidForm = () => {
      return formRef.current?.validate();
    };

    const getFormData = (data: IApiDataChangeRequestUser) => {
      const formData = new FormData();

      formData.append('beneficiaryBankName', data.beneficiaryBankName);
      formData.append('beneficiaryAccountNumber', data.beneficiaryAccountNumber);
      formData.append('bankBranchName', data.bankBranchName);
      formData.append('bankCardFileItem', data.bankCardFileItem);

      return formData;
    };

    const handleClose = () => {
      onClose?.(false);
    };

    const onSubmit = async (data: IFormApiDataChangeRequestUser) => {
      const isValid = isValidForm();

      if (!isValid || !data.bankCardFileItem) {
        return;
      }

      try {
        setProcessing(true);

        const formData = getFormData({ ...data, bankCardFileItem: data.bankCardFileItem });

        await apiDataChangeRequestUserService.create(formData);

        await onSuccess();

        NotificationDialog.show(keyMap.sharedMsgRecordSuccessSaved, 'success');

        handleClose();
      } catch (error) {
        handleError(keyMap.sharedErrorsFailedToCreateRecord, error);
      } finally {
        setProcessing(false);
      }
    };

    return (
      <div className={styles.form}>
        <Form className="form grid--form" form={formState} ref={formRef} onChange={handleChange} />
        <div className={styles.dropzoneWrapper}>
          {!formState.bankCardFileItem && (
            <DropZone
              title={dropzoneTitle ? dropzoneTitle : keyMap.profileWarningMessageBankCardDropzoneTitle}
              hintMessage={keyMap.profileWarningMessageBankCardDropzoneHint}
              params={{ onBeforeUploadStarted: (file: File) => setValue('bankCardFileItem', file) }}
            />
          )}
          {formState.bankCardFileItem && (
            <DocumentPreview
              fileName={formState.bankCardFileItem.name}
              onDeleteHandler={() => setValue('bankCardFileItem', null)}
            />
          )}
        </div>

        <div className={styles.formButtons}>
          <ButtonNew
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.bankCardFileItem}
            size="large"
            isLoading={processing}
            type="primaryMember">
            {i18n.t(keyMap.sharedActionsSubmit)}
          </ButtonNew>
          <ButtonNew onClick={handleClose} size="middle" type="linkMember">
            {i18n.t(keyMap.commonClose)}
          </ButtonNew>
        </div>
      </div>
    );
  },
);
